/**
 * F-011 — Slingshot input.
 *
 * Drag-to-aim, release-to-launch on a target HTMLElement. The vector from
 * pointer-down to current pointer (in screen space) is **inverted** to
 * give the slingshot's "pull back to launch forward" feel.
 *
 * Visual + power-tuning + cooldown live in dedicated modules (F-012/013/
 * 014); this file only owns the geometry of pointer → unit-vector +
 * normalized drag distance, plus the launch callback.
 */

const DEFAULT_MAX_DRAG_PX = 240;

export interface AimState {
  active: boolean;
  /** unit vector pointing from slingshot origin toward launch direction */
  direction: { x: number; y: number };
  /** 0..1 normalized drag distance (clamped to maxDragPx) */
  power: number;
}

export interface SlingshotEventMap {
  drag: AimState;
  release: AimState;
  cancel: void;
}

type Listener<E extends keyof SlingshotEventMap> = (
  payload: SlingshotEventMap[E],
) => void;

export interface Slingshot {
  detach(): void;
  on<E extends keyof SlingshotEventMap>(event: E, fn: Listener<E>): () => void;
  isActive(): boolean;
  current(): AimState;
}

export interface SlingshotOptions {
  maxDragPx?: number;
}

const ZERO_AIM: AimState = {
  active: false,
  direction: { x: 0, y: 0 },
  power: 0,
};

export function createSlingshot(
  target: HTMLElement,
  options: SlingshotOptions = {},
): Slingshot {
  const maxDragPx = options.maxDragPx ?? DEFAULT_MAX_DRAG_PX;

  let aim: AimState = { ...ZERO_AIM };
  let startX = 0;
  let startY = 0;

  const listeners: { [E in keyof SlingshotEventMap]: Set<Listener<E>> } = {
    drag: new Set(),
    release: new Set(),
    cancel: new Set(),
  };

  const emit = <E extends keyof SlingshotEventMap>(
    event: E,
    payload: SlingshotEventMap[E],
  ): void => {
    for (const fn of listeners[event]) fn(payload);
  };

  const computeAim = (clientX: number, clientY: number): AimState => {
    // Slingshot pulls *back* — invert so the planet flies away from the drag.
    const dx = startX - clientX;
    const dy = startY - clientY;
    const length = Math.hypot(dx, dy);
    if (length < 1e-3) return { active: true, direction: { x: 0, y: 0 }, power: 0 };
    const power = Math.min(length / maxDragPx, 1);
    return {
      active: true,
      direction: { x: dx / length, y: dy / length },
      power,
    };
  };

  const onPointerDown = (event: PointerEvent): void => {
    if (event.button !== 0 && event.pointerType !== "touch") return;
    if (aim.active) return;
    target.setPointerCapture?.(event.pointerId);
    startX = event.clientX;
    startY = event.clientY;
    aim = { active: true, direction: { x: 0, y: 0 }, power: 0 };
    emit("drag", aim);
  };

  const onPointerMove = (event: PointerEvent): void => {
    if (!aim.active) return;
    aim = computeAim(event.clientX, event.clientY);
    emit("drag", aim);
  };

  const onPointerUp = (event: PointerEvent): void => {
    if (!aim.active) return;
    aim = computeAim(event.clientX, event.clientY);
    const snapshot = aim;
    aim = { ...ZERO_AIM };
    emit("release", snapshot);
  };

  const onPointerCancel = (): void => {
    if (!aim.active) return;
    aim = { ...ZERO_AIM };
    emit("cancel", undefined);
  };

  target.addEventListener("pointerdown", onPointerDown);
  target.addEventListener("pointermove", onPointerMove);
  target.addEventListener("pointerup", onPointerUp);
  target.addEventListener("pointercancel", onPointerCancel);

  const detach = (): void => {
    target.removeEventListener("pointerdown", onPointerDown);
    target.removeEventListener("pointermove", onPointerMove);
    target.removeEventListener("pointerup", onPointerUp);
    target.removeEventListener("pointercancel", onPointerCancel);
  };

  const on = <E extends keyof SlingshotEventMap>(
    event: E,
    fn: Listener<E>,
  ): (() => void) => {
    listeners[event].add(fn);
    return () => listeners[event].delete(fn);
  };

  return {
    detach,
    on,
    isActive: () => aim.active,
    current: () => aim,
  };
}

export const __testing = { DEFAULT_MAX_DRAG_PX };
